import mapboxgl from "mapbox-gl";
import EventEmitter from "events";
import { GetAllPlugins } from "./plugins";
import { Task } from "@emberly/zenith-client";
import { getLocalStorageValue, setLocalStorageValue } from "../../../../common/helpers";
import { verifiedIsMobile } from "../../../../providers/DeviceProvider";

export default class OperationsMap extends EventEmitter {

  constructor(id, conf = {}) {
    super();

    const { settings, container, station, theme, t, getAccessToken, storage } = conf;

    this.id = id;
    this.t = t;
    this.filteredTasks = [];
    this.completedTasks = [];
    this.vehicles = [];
    this.fleetIntegrations = [];
    this.station = station;
    this.tasksLoaded = false;
    this.mobile = verifiedIsMobile;

    this._getAccessToken = getAccessToken;
    this._container = container;
    this._hasPopups = false;

    this.theme = theme;
    this.images = new Set();
    this.models = new Map();

    this.useLocalStorage = storage === "localStorage";
    this.settings = settings;

    this.styleUrl = this.settings.styleUrl || "mapbox://styles/mapbox/standard";
    this.styleId = this.settings.styleId || "standard";

    this._map = new mapboxgl.Map({
      container,
      style: this.styleUrl,
      center: station?.location?.coordinates,
      zoom: 11,
      attributionControl: false,
      logoPosition: "bottom"
    });


    this._ro = new ResizeObserver(() => {
      if (!this._destroyed) {
        requestAnimationFrame(() => {
          try {
            this._map?.resize();
          } catch { }
        });
      }
    });

    this._ro.observe(this._container);
    this._destroyed = false;

    this.activePlugins = new Set(["mylocations", "vehicles", "dispatch"]);
    this._plugins = GetAllPlugins(this);
    this._settingsTimer = null;

    this._map.on("load", () => this.setup());
  }


  setup() {
    if (this._destroyed) return;

    this.addSlotLayers();
    this.activePlugins = !!this.settings?.activePlugins ? new Set(this.settings.activePlugins) : this.activePlugins;

    for (let i = 0; i < this._plugins.length; i++) {
      let plugin = this._plugins[i];
      let settings = this.settings?.plugins?.[plugin.name];

      if (!!settings && typeof settings === "object") {
        plugin.setSettings(settings);
      } else {
        plugin.setSettings(plugin.defaultSettings);
      }
    }

    this.mountPlugins();

    // Remove popups when map moves
    this._map.on("move", () => {
      this.clearPopups();
    });

    this.emit("loaded", true);
  }

  clearPopups() {
    if (this._hasPopups) {
      this._hasPopups = false;
      for (let i = 0; i < this._plugins.length; i++) {
        this._plugins[i].clearPopups();
      }
    }
  }

  readSettings() {
    try {
      let data = getLocalStorageValue(`operationmap_settings_${this.id}`, "{}");
      return JSON.parse(data) || {};
    } catch (err) {
      console.log(err);
    }
  }

  static ReadSettings(id) {
    try {
      let data = getLocalStorageValue(`operationmap_settings_${id}`, "{}");
      return JSON.parse(data) || {};
    } catch (err) {
      console.log(err);
    }
  }


  writeSettings() {

    let settings = {
      activePlugins: [...this.activePlugins],
      plugins: {},
      styleUrl: this.styleUrl,
      styleId: this.styleId,
    };

    for (let i = 0; i < this._plugins.length; i++) {
      let plugin = this._plugins[i];
      settings.plugins[plugin.name] = plugin.settings;
    }

    setLocalStorageValue(`operationmap_settings_${this.id}`, JSON.stringify(settings));
  }

  resetSettings() {
    for (let i = 0; i < this._plugins.length; i++) {
      this._plugins[i].resetSettings();
    }

    this.activePlugins = new Set(["mylocations", "vehicles", "dispatch"]);

    this.unMountPlugins();
    this.mountPlugins();

    setTimeout(() => {
      this.pollWriteSettings();
      this.emit("reset");
    }, 1);
  }

  setStyle(style) {
    if (this._destroyed) return;

    this.unMountPlugins();

    this._map.once("style.load", async () => {
      [...this.models].forEach(kv => this.addModel(kv[0], kv[1], true));
      await Promise.all([...this.images].map(name => this.addImage(name, true)));

      this.addSlotLayers();
      this.mountPlugins();
    });

    this.styleUrl = style.url;
    this.styleId = style.id;
    this._map.setStyle(this.styleUrl);
    this.pollWriteSettings();
  }


  addSlotLayers() {

    if (this.styleId !== "standard") {
      this._map.addLayer({ type: "slot", id: "bottom" });
      this._map.addLayer({ type: "slot", id: "middle" });
      this._map.addLayer({ type: "slot", id: "top" });
    }

    for (let i = 1; i <= 3; i++) {
      this._map.addLayer({ type: "slot", id: `elevation-${i}` });
    }
  }

  mountPlugins() {

    for (let i = 0; i < this._plugins.length; i++) {
      let plugin = this._plugins[i];

      if (this.isPluginActive(plugin.name)) {
        plugin.mount();
      }
    }
  }

  unMountPlugins() {
    for (let i = 0; i < this._plugins.length; i++) {
      let plugin = this._plugins[i];
      plugin.unMount();
    }
  }

  activatePlugin(pluginName) {
    if (!this.isPluginActive(pluginName)) {
      this.activePlugins.add(pluginName);
      const p = this.getPlugin(pluginName);
      p.mount();
      this.pollWriteSettings();
    }
  }

  deactivatePlugin(pluginName) {
    if (this.isPluginActive(pluginName)) {
      this.activePlugins.delete(pluginName);
      const p = this.getPlugin(pluginName);
      p.unMount();
      this.pollWriteSettings();
    }
  }

  isPluginActive(pluginName) {
    return this.activePlugins.has(pluginName);
  }


  async getAccessToken() {
    try {
      return await this._getAccessToken();
    } catch (err) {
      console.log(err);
      return null;
    }
  }

  destroy() {
    clearTimeout(this._settingsTimer);
    this.removeAllListeners();
    this._destroyed = true;
    this._map?.remove();
    this._ro?.disconnect();
  }

  addImage(name, redo = false) {
    if (this._destroyed) return;

    const task = new Task();


    if (!this.images.has(name) || redo) {
      this.images.add(name);

      if (!this._map.hasImage(name)) {

        this._map.loadImage(
          `/assets/icons/map/${name}.png`,
          (error, image) => {
            if (!!image && !this._map.hasImage(name)) {
              this._map?.addImage(name, image, { sdf: true, pixelRatio: 16 });
            }
            task.complete();
          }
        );

      } else {
        task.complete();
      }
    }

    return task.wait();
  }


  addModel(name, url, redo = false) {
    if (this._destroyed) return;

    if (!this.models.has(name) || redo) {
      this.models.set(name, url);
      this._map.addModel(name, `${window.location.origin}/assets/models/${url}`);
    }
  }


  zoomIn() {
    if (this._destroyed) return;

    this._map.zoomIn();
  }

  zoomOut() {
    if (this._destroyed) return;
    this._map.zoomOut();
  }

  recenter() {
    if (this._destroyed || !this.station?.location?.coordinates) return;

    if (this.filteredTasks.length === 0) {
      this._map.flyTo({
        center: this.station.location.coordinates,
        zoom: 11,
        padding: 64
      });
    } else {
      // TODO

      const bounds = new mapboxgl.LngLatBounds();
      let hasBounds = false;

      bounds.extend(this.station.location.coordinates);

      for (let i = 0; i < this.filteredTasks.length; i++) {
        const waypoints = this.filteredTasks[i].route.waypoints;

        if (!waypoints || waypoints.length === 0 || waypoints[0]?.coordinates?.length !== 2) continue;
        bounds.extend(waypoints[0].coordinates);
        hasBounds = true;
      }

      if (hasBounds) {
        this._map?.fitBounds(bounds, { padding: 64, maxZoom: 12 });
      } else {
        this._map.flyTo({
          center: this.station.location.coordinates,
          zoom: 11,
          padding: 64
        });
      }
    }

  }

  updateDispatchData(filteredTasks, completedTasks) {
    if (this._destroyed) return;


    this.filteredTasks = filteredTasks;
    this.completedTasks = completedTasks;
    this._plugins.forEach(plugin => plugin.updateDispatchData(filteredTasks, completedTasks));

    if (!this.tasksLoaded && this.filteredTasks.length !== 0) {
      this.tasksLoaded = true;
      this.recenter();
    }

  }

  updateVehicleData(vehicles) {
    if (this._destroyed) return;

    this.vehicles = vehicles;
    this._plugins.forEach(plugin => plugin.updateVehicleData(vehicles));
  }


  updateFleetIntegrations(fleetIntegrations) {
    if (this._destroyed) return;

    this.fleetIntegrations = fleetIntegrations;
    this._plugins.forEach(plugin => plugin.updateFleetIntegrations(fleetIntegrations));
  }

  pollWriteSettings() {
    if (this.useLocalStorage) {
      clearTimeout(this._settingsTimer);
      this._settingsTimer = setTimeout(() => this.writeSettings(), 500);
    }
  }

  updateSettings(pluginName, settings = {}) {
    const plugin = this._plugins.find(t => t.name === pluginName);
    plugin?.updateSettings(settings);
    this.pollWriteSettings();
  }

  getSettings(pluginName) {
    return this.getPlugin(pluginName)?.settings || {};
  }

  getPlugin(pluginName) {
    return this._plugins.find(t => t.name === pluginName)
  }

  hasFleetIntegrations() {
    return this.fleetIntegrations?.length > 0;
  }

}